import { SLHeaders } from '@travelwin/core';
import pino, { Logger } from 'pino';

import { obfuscateSensitiveField } from '../../middleware/obfuscate-sensitive-fields';
import { isClient } from '../../utils/ssrUtils';

const baseContext = {
  layerIdentifier: 'travelwin-gw-ecommerce-ssr',
  logger_name: 'logger-ssr',
};

let pinoInstance: Logger<never, boolean>;
if (!isClient()) {
  const LEVEL = process.env.LOG_LEVEL || 'debug';

  const formatLevel = (label: string, number: number) => {
    return { level: label, level_value: number };
  };

  pinoInstance = pino({
    level: LEVEL,
    base: null,
    messageKey: 'message',
    timestamp: () =>
      pino.stdTimeFunctions.isoTime().replace('time', '@timestamp'),
    formatters: {
      level: formatLevel,
    },
  });
}

const extractReqInitAttributes = (reqInit: RequestInit) => {
  let auxRecord: Record<string, string> = {};

  if (reqInit.headers instanceof Headers) {
    reqInit.headers.forEach((value, key) => {
      auxRecord[key] = value;
    });
  } else if (Array.isArray(reqInit.headers)) {
    reqInit.headers.forEach(([key, value]) => (auxRecord[key] = value));
  } else {
    auxRecord = reqInit.headers as Record<string, string>;
  }

  return {
    correlationId: auxRecord[SLHeaders.CORRELATION_ID],
    requestId: auxRecord[SLHeaders.REQUEST_ID],
    appOrigin: auxRecord[SLHeaders.APP_ORIGIN],
    clientUiVersion: auxRecord[SLHeaders.APP_VERSION],
    sessionId: auxRecord[SLHeaders.SESSION_ID],
    origin: auxRecord[SLHeaders.ORIGIN],
  };
};

const headersToObject = (headers?: Headers) => {
  if (!headers) return {};
  const headersObj: Record<string, string> = {};
  for (const [key, value] of headers.entries()) {
    headersObj[key] = obfuscateSensitiveField(key, value);
  }

  return headersObj;
};

export const logHttp = ({
  url,
  requestInit,
  response,
  startTime,
  extraAttributes,
}: {
  url: string | URL;
  requestInit: RequestInit;
  response: Response;
  startTime: number;
  extraAttributes?: object;
}) => {
  if (!pinoInstance) {
    return;
  }

  try {
    const responseTime = performance.now() - startTime;

    const context = {
      ...baseContext,
      httpPath: url,
      httpMethod: requestInit.method || 'GET',
      httpStatusCode: response.status.toString(),
      ...extractReqInitAttributes(requestInit),
      ...(extraAttributes || {}),
      responseTime,
      additionalData: {
        req: requestInit,
        res: { headers: headersToObject(response.headers) },
      },
    };

    pinoInstance.info(context);
  } catch (e) {
    console.error('Logger error', e);
  }
};

export const logCachedResponse = ({
  key,
  url,
  requestInit,
  extraAttributes,
  startTime,
  cachedContent,
}: {
  url: string | URL;
  key: string;
  requestInit: RequestInit;
  extraAttributes?: object;
  startTime: number;
  cachedContent: string;
}) => {
  if (!pinoInstance) {
    return;
  }

  try {
    const responseTime = performance.now() - startTime;

    const context = {
      ...baseContext,
      httpPath: url,
      httpMethod: 'GET',
      httpStatusCode: '200',
      ...extractReqInitAttributes(requestInit),
      ...(extraAttributes || {}),
      responseTime,
      additionalData: {
        req: requestInit,
        cache: { key, cachedContent },
      },
    };

    pinoInstance.debug(context);
  } catch (e) {
    console.error('Logger error', e);
  }
};

export const logSSRError = ({
  error,
  request,
  errorCode,
  startTime,
  extraAttributes,
}: {
  error: unknown;
  request?: Request;
  errorCode: string;
  startTime?: number;
  extraAttributes?: Record<string, string>;
}) => {
  if (!pinoInstance) {
    return;
  }

  try {
    const responseTime = startTime ? performance.now() - startTime : undefined;

    const context = {
      ...baseContext,
      httpPath: request?.url,
      httpMethod: request?.method,
      ...extractReqInitAttributes({ headers: request?.headers }),
      errorCode,
      responseTime,
      additionalData: {
        req: { headers: headersToObject(request?.headers) },
        error,
        ...(extraAttributes || {}),
      },
    };

    const message = error instanceof Error ? error.message : error;
    pinoInstance.error(context, JSON.stringify(message));
  } catch (e) {
    console.error('Logger error', e);
  }
};
